import React from 'react';
import { Rect, Circle, Line, Text, Group } from 'react-konva';
import { getCylinderPoints } from '../../utils/geometryUtils';

/**
 * Компонент для отображения «цилиндра» (фактически прямоугольника),
 * со всей логикой: drag, resize, подсветка, dimension-lines и т.д.
 * 
 * @param {object} props
 * @param {object} props.cylinder - Один объект из массива cylinders: { id, x, y, width, height, locked }
 * @param {boolean} props.isDimensionLineEnabled - Включён ли режим размерных линий
 * @param {object|null} props.highlightedSide - Данные о подсвеченной стороне (type, x, y, objectId, objectType), если есть
 * @param {function} props.setHighlightedSide - Setter для highlightedSide
 * @param {object|null} props.highlightedPoint - Данные о подсвеченной точке (x, y, idx, objectId, objectType)
 * @param {function} props.setHighlightedPoint - Setter для highlightedPoint
 * 
 * @param {function} props.handleMouseOver - Коллбек, который вызывается при onMouseOver/onMouseMove (для подсветки сторон)
 * @param {function} props.handleResizeStart - Коллбек "начало изменения размера"
 * @param {function} props.handleResize - Коллбек "процесс изменения размера"
 * @param {function} props.handleResizeEnd - Коллбек "конец изменения размера"
 * 
 * @param {function} props.handleDragStart - Коллбек "начало drag"
 * @param {function} props.handleDragMove - Коллбек "процесс drag"
 * @param {function} props.handleDragEnd - Коллбек "конец drag"
 * 
 * @param {function} props.onSelectCylinder - Функция, вызываемая при клике на цилиндр (чтобы выделить его)
 * @param {boolean} props.isSelected - Флаг, выделен ли данный цилиндр (меняем цвет обводки, например)
 */
function CylinderComponent({
  cylinder,
  isDimensionLineEnabled,
  highlightedSide,
  setHighlightedSide,
  highlightedPoint,
  setHighlightedPoint,
  snappingMode,

  isLKeyPressed,
  isPKeyPressed = false,
  pAttachmentMode = 'off', // 'off', 'points', 'edges'
  isSelected,
  isTransforming,

  handleMouseOver,
  handleResizeStart,
  handleResize,
  handleResizeEnd,

  handleDragStart,
  handleDragMove,
  handleDragEnd,
  
  handleSnapPointClick,
  handleEdgeClick,
  pointAttachmentMode,
  isPointConstrained,
  isEdgeConstrained,
  handleRemoveConstraint,
  handleRemoveEdgeConstraint,

  onSelectCylinder,
  zoom = 1,
  isPatternSelected = false,
  getSnappedPosition,
}) {
  const { id, x, y, width, height, locked, shape = 'round' } = cylinder;

  // При наведении курсора вне rect-а сбрасываем подсветку стороны
  const handleMouseOut = () => {
    setHighlightedSide(null);
  };

  // Цвет обводки, если объект выделен
  const strokeColor = isSelected || isPatternSelected ? 'blue' : '#000000';
  
  // Как у фитингов (FittingComponent renderShape), чтобы линии выглядели одинаково
  const strokeWidth = 1;
  
  // Без заливки для всех труб
  const fillColor = 'rgba(255, 255, 255, 0)';

  return (
    <>
      {/* Основной прямоугольник цилиндра */}
      <Rect
        id={id}
        x={x}
        y={y}
        width={width}
        height={height}
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeScaleEnabled={false}
        cornerRadius={shape === 'square' ? 0 : undefined} // Квадратные без скруглений
        draggable={!locked} // Если locked - нельзя двигать
        // Для dimension lines подключаем handleMouseOver, handleMouseMove
        onMouseOver={isDimensionLineEnabled ? handleMouseOver : undefined}
        onMouseMove={isDimensionLineEnabled ? handleMouseOver : undefined}
        onMouseOut={handleMouseOut}
        // Drag
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        // Выделение
        onClick={(e) => {
          e.cancelBubble = true; 
          // Сигнализируем наверх, что именно этот цилиндр выбран
          onSelectCylinder(id);
        }}
      />

      {/* Если цилиндр зафиксирован, рисуем замочек только при зажатой клавише L */}
      {locked && isLKeyPressed && (
        <Text
          x={x + width / 2 - 8}
          y={y + height / 2 - 8}
          text="🔒"
          fontSize={16}
          fill="#000000"
          listening={false}
        />
      )}

      {/*
        Точки для привязки.
        Рендерим только если включен режим привязки точек (pAttachmentMode === 'points') или snappingMode === 'points'.
      */}
      {(snappingMode === 'points' || pAttachmentMode === 'points') &&
        getCylinderPoints({ x, y, width, height }).map((point, idx) => {
          // Проверяем, выбрана ли эта точка как первая в режиме привязки
          const isFirstPoint = pointAttachmentMode?.firstPoint && 
                               pointAttachmentMode.firstPoint.objectId === id && 
                               pointAttachmentMode.firstPoint.pointIndex === idx;
          
          const isConstrained = isPointConstrained && isPointConstrained(id, 'cylinder', idx);
          
          const isInPointsMode = pAttachmentMode === 'points';
          
          return (
            <React.Fragment key={`${id}-point-${idx}-snap-group`}>
              <Circle
                key={`${id}-point-${idx}-snap`}
                x={point.x}
                y={point.y}
                radius={isInPointsMode ? 5 : 3} // Увеличиваем радиус в режиме привязки точек
                fill={isFirstPoint ? '#ff0000' : (isInPointsMode ? '#00ff00' : 'green')} // Красная если выбрана, ярко-зеленая в режиме привязки
                stroke={isInPointsMode ? '#ffffff' : undefined} // Белая обводка в режиме привязки
                strokeWidth={isInPointsMode ? 1 : 0}
                hitStrokeWidth={0}
                name="snap-point"
                listening
                onClick={(evt) => {
                  if (isInPointsMode && handleSnapPointClick) {
                    evt.cancelBubble = true; // Останавливаем всплытие события
                    
                    // Получаем абсолютные координаты точки на Stage с учетом трансформации
                    const circle = evt.target;
                    const absPos = circle.getAbsolutePosition();
                    
                    handleSnapPointClick(id, 'cylinder', idx, absPos.x, absPos.y);
                  }
                }}
                onMouseDown={(evt) => {
                  if (isInPointsMode) {
                    // В режиме привязки точек не начинаем drag
                    evt.cancelBubble = true;
                    return;
                  }
                  if (!isDimensionLineEnabled) {
                    const group = evt.target.getParent();
                    if (group && typeof group.findOne === 'function') {
                      const rectNode = group.findOne('#' + id);
                      if (rectNode && typeof rectNode.startDrag === 'function') {
                        rectNode.startDrag();
                      }
                    }
                  }
                }}
                style={{ cursor: isInPointsMode ? 'pointer' : 'default' }}
              />
              
              {/* Визуальный индикатор привязки - показывается только в режиме привязки точек и если точка привязана */}
              {isInPointsMode && isConstrained && (
                <Group
                  x={point.x + 8}
                  y={point.y - 8}
                  onClick={(evt) => {
                    evt.cancelBubble = true;
                    if (handleRemoveConstraint) {
                      handleRemoveConstraint(id, 'cylinder', idx);
                    }
                  }}
                  onMouseDown={(evt) => evt.cancelBubble = true}
                  listening
                  style={{ cursor: 'pointer' }}
                >
                  {/* Фон индикатора */}
                  <Circle
                    radius={6}
                    fill="#ffffff"
                    stroke="#ff9900"
                    strokeWidth={2}
                  />
                  {/* Иконка "звено цепи" */}
                  <Line
                    points={[-3, -2, -1, -2, -1, 0, 1, 0, 1, 2, 3, 2]}
                    stroke="#ff9900"
                    strokeWidth={1.5}
                    lineCap="round"
                    lineJoin="round"
                  />
                </Group>
              )}
            </React.Fragment>
          );
        })}

      {/* Визуализация стенок в режиме привязки стенок (показываем и для залоченных объектов) */}
      {pAttachmentMode === 'edges' && (() => {
        const edges = [
          { type: 'top', points: [x, y, x + width, y], midX: x + width / 2, midY: y },
          { type: 'bottom', points: [x, y + height, x + width, y + height], midX: x + width / 2, midY: y + height },
          { type: 'left', points: [x, y, x, y + height], midX: x, midY: y + height / 2 },
          { type: 'right', points: [x + width, y, x + width, y + height], midX: x + width, midY: y + height / 2 },
        ];

        const isFirstEdge = (localEdgeType) => {
          if (!pointAttachmentMode?.firstPoint?.isEdge || pointAttachmentMode.firstPoint.objectId !== id) {
            return false;
          }
          // Теперь в pointAttachmentMode хранится локальный тип, просто сравниваем
          return pointAttachmentMode.firstPoint.edgeType === localEdgeType;
        };

        return edges.map((edge) => {
          const isSelected = isFirstEdge(edge.type);
          // Для isConstrained используем локальный тип (constraints теперь хранят локальные типы)
          const isConstrained = isEdgeConstrained && isEdgeConstrained(id, 'cylinder', edge.type);
          
          return (
            <React.Fragment key={`${id}-edge-${edge.type}`}>
              {/* Толстая невидимая линия для удобного клика */}
              <Line
                points={edge.points}
                stroke="rgba(0,0,0,0)"
                strokeWidth={20}
                hitStrokeWidth={20}
                listening
                onClick={(evt) => {
                  if (pAttachmentMode === 'edges' && handleEdgeClick) {
                    evt.cancelBubble = true;
                    const line = evt.target;
                    // Используем центр стенки
                    const centerX = edge.midX;
                    const centerY = edge.midY;
                    const group = line.getParent();
                    const t = group.getAbsoluteTransform();
                    const absCenter = t.point({ x: centerX, y: centerY });
                    // Передаем ЛОКАЛЬНЫЙ тип стенки (edge.type), чтобы он корректно работал при повороте объекта
                    handleEdgeClick(id, 'cylinder', edge.type, absCenter.x, absCenter.y);
                  }
                }}
                onMouseOver={(evt) => {
                  evt.target.getStage().container().style.cursor = 'pointer';
                }}
                onMouseOut={(evt) => {
                  evt.target.getStage().container().style.cursor = 'default';
                }}
              />
              {/* Видимая линия для визуализации */}
              <Line
                points={edge.points}
                stroke={isSelected ? '#ff0000' : (isConstrained ? '#ffaa00' : '#00ff00')}
                strokeWidth={isSelected ? 3 : (isConstrained ? 2.5 : 2)}
                listening={false}
                dash={[5, 5]}
              />
              {/* Индикатор привязки стенки - показывается только если стенка привязана */}
              {isConstrained && (
                <Group
                  x={edge.midX}
                  y={edge.midY}
                  onClick={(evt) => {
                    evt.cancelBubble = true;
                    if (handleRemoveEdgeConstraint) {
                      handleRemoveEdgeConstraint(id, 'cylinder', edge.type);
                    }
                  }}
                  onMouseDown={(evt) => evt.cancelBubble = true}
                  listening
                  style={{ cursor: 'pointer' }}
                >
                  {/* Фон индикатора */}
                  <Circle
                    radius={8}
                    fill="#ffffff"
                    stroke="#ffaa00"
                    strokeWidth={2}
                  />
                  {/* Иконка "звено цепи" */}
                  <Line
                    points={[-4, -2, -2, -2, -2, 0, 2, 0, 2, 2, 4, 2]}
                    stroke="#ffaa00"
                    strokeWidth={1.5}
                    lineCap="round"
                    lineJoin="round"
                  />
                </Group>
              )}
            </React.Fragment>
          );
        });
      })()}

      {isDimensionLineEnabled &&
        getCylinderPoints({ x, y, width, height }).map((point, idx) => (
          <Circle
            key={`${id}-point-${idx}-dim`}
            x={point.x}
            y={point.y}
            radius={5}
            fill={
              highlightedPoint &&
              highlightedPoint.objectId === id &&
              highlightedPoint.idx === idx
                ? 'red'
                : 'rgba(0,0,0,0)'
            }
            hitStrokeWidth={0}
            onMouseOver={(evt) => {
              const group = evt.target.getParent();
              const t = group.getAbsoluteTransform();
              const abs = t.point({ x: point.x, y: point.y });
              const z = zoom || 1;
              setHighlightedPoint({
                x: abs.x / z,
                y: abs.y / z,
                objectId: id,
                objectType: 'cylinder',
                idx,
              });
            }}
            onMouseOut={() => {
              setHighlightedPoint(null);
            }}
          />
        ))}

      {/* Маркер изменения размеров — размещён после точек, чтобы быть поверх */}
      {!isDimensionLineEnabled && !locked && !isSelected && !isTransforming && !isPKeyPressed && (
        <Circle
          name="resize-handle"
          x={x + width}
          y={y + height}
          radius={5}
          fill="white"
          stroke="#000000"
          strokeWidth={2}
          draggable
          dragBoundFunc={function (pos) {
            const group = this.getParent();
            const t = group.getAbsoluteTransform();
            const inv = t.copy().invert();
            const z = zoom || 1;
            
            // Применяем привязку в мировых координатах Stage (до перевода в локальные)
            let finalPos = { x: pos.x, y: pos.y };
            if (typeof getSnappedPosition === 'function' && snappingMode === 'points') {
              const snapped = getSnappedPosition(pos.x / z, pos.y / z, id, width, height);
              finalPos = { x: snapped.x * z, y: snapped.y * z };
            }
            
            // Переводим в локальные координаты после привязки
            const local = inv.point(finalPos);
            
            // Проверяем минимальные размеры в локальных координатах
            if (local.x < x + 20) local.x = x + 20;
            if (local.y < y + 20) local.y = y + 20;
            
            // Переводим обратно в абсолютные координаты
            const abs = t.point(local);
            return { x: abs.x, y: abs.y };
          }}
          onDragStart={handleResizeStart}
          onDragMove={(evt) => handleResize(evt, id)}
          onDragEnd={handleResizeEnd}
          listening={true}
        />
      )}

      {/*
        Подсветка стороны (красная линия), если пользователь навёлся на сторону 
        и highlightedSide.objectId === id. 
        В вашем Canvas.js side может быть {type: 'top'|'bottom'|'left'|'right', x, y, objectId}.
      */}
      {isDimensionLineEnabled &&
        highlightedSide &&
        highlightedSide.objectType === 'cylinder' &&
        highlightedSide.objectId === id && (
          <Line
            points={
              highlightedSide.type === 'top'
                ? [x, y, x + width, y]
                : highlightedSide.type === 'bottom'
                ? [x, y + height, x + width, y + height]
                : highlightedSide.type === 'left'
                ? [x, y, x, y + height]
                : [x + width, y, x + width, y + height]
            }
            stroke="#ff0000"
            strokeWidth={2}
          />
        )}
    </>
  );
}

export default CylinderComponent;
